import React from "react";
import { ScrollView, StyleSheet } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { View } from "../components/Themed";
import LegalDocument from "../components/LegalDocument";
import PublicFooter from "../components/PublicFooter";
import Config from "../constants/Config";
import i18n from "../utils/localization";

function buildBlocks(legal) {
  return [
    { type: "p", text: i18n.t("This Policy explains your rights and our obligations when you order food, drinks or services from {companyName}, operating under the brand {tradingAs} (\u201Cwe\u201D, \u201Cus\u201D, \u201Cour\u201D). It is written in line with the Bulgarian Consumer Protection Act (Закон за защита на потребителите, ЗЗП), the EU Consumer Rights Directive 2011/83/EU, and applicable Bulgarian commercial law.")
      .replace("{companyName}", legal.companyName)
      .replace("{tradingAs}", legal.tradingAs) },

    { type: "h2", text: i18n.t("1. Scope") },
    { type: "p", text: i18n.t("This Policy applies to all purchases made from {tradingAs}, including:")
      .replace("{tradingAs}", legal.tradingAs) },
    { type: "list", items: [
      i18n.t("On-premise dining ordered at the table, at the counter, or via in-venue QR-code self-service;"),
      i18n.t("Online orders for pickup placed through our website, app, or partner ordering channels;"),
      i18n.t("Online orders for delivery to an address you provide;"),
      i18n.t("Optional add-on services such as gaming, VR, and esports modules purchased on-site or online."),
    ]},
    { type: "p", text: i18n.t("It covers the entire payment lifecycle, regardless of whether payment was made in cash, by card with our staff or courier, or online via Stripe or myPOS.") },

    { type: "h2", text: i18n.t("2. Cancellation of orders") },
    { type: "h3", text: i18n.t("2.1 Cancellation by the customer") },
    { type: "p", text: i18n.t("You may cancel an order free of charge at any time before preparation begins. To cancel, contact us immediately using the details in Section 6 and quote your order number.") },
    { type: "p", text: i18n.t("Once preparation has started, cancellation is at the operator's discretion, because ingredients have already been committed and food may already be cooking. In such cases:") },
    { type: "list", items: [
      i18n.t("If we have not yet incurred significant cost, we may still cancel and refund you in full or in part;"),
      i18n.t("If the order is substantially prepared, we may be unable to refund it."),
    ]},
    { type: "p", text: i18n.t("For scheduled orders (placed in advance for a later time), you can cancel at no cost up until our standard preparation lead time before the scheduled slot.") },
    { type: "h3", text: i18n.t("2.2 Cancellation by us") },
    { type: "p", text: i18n.t("We may cancel an order, in whole or in part, for legitimate reasons including:") },
    { type: "list", items: [
      i18n.t("An item is out of stock and no acceptable substitute is available;"),
      i18n.t("We cannot deliver to your address (e.g., outside delivery zone, access blocked, recipient unreachable);"),
      i18n.t("The order is suspected of being fraudulent or placed in breach of our Terms;"),
      i18n.t("Force majeure events, equipment failure, or staff incapacity."),
    ]},
    { type: "p", text: i18n.t("In any such case, you will receive a full refund of any amounts already paid, by the original payment method, within the timelines in Section 5.") },

    { type: "h2", text: i18n.t("3. The 14-day withdrawal right does not apply to prepared food and drink") },
    { type: "p", text: i18n.t("EU and Bulgarian consumer law normally gives consumers buying at a distance (e.g., online) a 14-day right of withdrawal without giving any reason.") },
    { type: "p", text: i18n.t("However, under Article 16(d) of Directive 2011/83/EU, transposed into Bulgarian law via the ЗЗП, this right does not apply to goods that are liable to deteriorate or expire rapidly. Prepared meals, freshly cooked food, opened beverages, and other perishable items fall squarely within this exemption.") },
    { type: "p", text: i18n.t("In plain language: once we have prepared and handed over (or delivered) food and drinks to you, you cannot return them simply because you changed your mind, were no longer hungry, or did not like the taste in the absence of a genuine defect.") },
    { type: "p", text: i18n.t("This exemption is strictly about change of mind. It does not affect your statutory rights when something is genuinely wrong with your order — see Section 4.") },

    { type: "h2", text: i18n.t("4. Defective, incorrect, missing or non-delivered orders") },
    { type: "p", text: i18n.t("You are always entitled to a remedy where:") },
    { type: "list", items: [
      i18n.t("An item is missing from the order delivered or handed to you;"),
      i18n.t("An item is incorrect (different from what was ordered);"),
      i18n.t("An item is defective — for example, cold when it should be hot, spoiled, contaminated by a foreign object, undercooked, or otherwise not of satisfactory quality;"),
      i18n.t("The order was never delivered, or arrived unreasonably late and could no longer be consumed."),
    ]},
    { type: "p", text: i18n.t("Depending on the circumstances and your preference, we will offer one of the following remedies:") },
    { type: "list", items: [
      i18n.t("Replacement of the affected item(s) as soon as reasonably possible;"),
      i18n.t("Partial refund corresponding to the affected item(s);"),
      i18n.t("Full refund of the order if it cannot reasonably be remedied."),
    ]},
    { type: "h3", text: i18n.t("How to report an issue") },
    { type: "p", text: i18n.t("To allow us to verify and resolve the issue fairly, please report it as soon as possible:") },
    { type: "list", items: [
      i18n.t("On-premise orders — before you leave the venue, by speaking to a staff member or manager;"),
      i18n.t("Pickup orders — at the counter at the time of pickup, or within 2 hours of collection;"),
      i18n.t("Delivery orders — at the door with the courier where possible, and in any case within 24 hours of delivery."),
    ]},
    { type: "p", text: i18n.t("When reporting, please provide your order number, a brief description of the issue, and where possible a photograph of the affected item(s). Reports made significantly outside these windows may be harder for us to substantiate, but we will still review them in good faith.") },

    { type: "h2", text: i18n.t("5. Refund methods and timelines") },
    { type: "list", items: [
      i18n.t("Refunds are processed to the original payment method used for the order."),
      i18n.t("Card payments (Stripe / myPOS) are refunded to the same card. Funds typically appear within 3–10 business days, depending on your bank."),
      i18n.t("Cash payments (pay-at-pickup or pay-on-delivery in cash) are refunded in cash at the venue, or by another method we agree with you in writing."),
      i18n.t("We will process accepted refunds without undue delay, and in any event within 14 days of accepting your claim, as required by Bulgarian law."),
    ]},
    { type: "p", text: i18n.t("We do not charge any fee for processing legitimate refunds.") },

    { type: "h2", text: i18n.t("6. How to request a refund or report a problem") },
    { type: "p", text: i18n.t("Please contact us with your order number and a description of the issue:") },
    { type: "list", items: [
      i18n.t("Email: {email}").replace("{email}", legal.email),
      i18n.t("Phone: {phone}").replace("{phone}", legal.phone),
      i18n.t("In person: at the venue during opening hours"),
    ]},
    { type: "p", text: i18n.t("We aim to acknowledge complaints within 2 business days and to resolve them within 14 days.") },

    { type: "h2", text: i18n.t("7. Disputes and out-of-court resolution") },
    { type: "p", text: i18n.t("If you are not satisfied with our response, you have the right to escalate:") },
    { type: "list", items: [
      i18n.t("{consumerAuthorityName} — {consumerAuthorityUrl} — the competent authority for consumer complaints in Bulgaria;")
        .replace("{consumerAuthorityName}", legal.consumerAuthority.name)
        .replace("{consumerAuthorityUrl}", legal.consumerAuthority.url),
      i18n.t("EU Online Dispute Resolution (ODR) platform — {odrUrl} — for online purchases, providing access to alternative dispute resolution across the EU.")
        .replace("{odrUrl}", legal.odrUrl),
    ]},
    { type: "p", text: i18n.t("Use of these mechanisms is voluntary and without prejudice to your right to bring proceedings before the competent Bulgarian courts.") },

    { type: "h2", text: i18n.t("8. Company contact") },
    { type: "p", text: i18n.t("{companyName}\nTrading as: {tradingAs}\nTax ID ({taxIdLabel}): {taxId}\nRegistered address: {address}\nEmail: {email}\nPhone: {phone}")
      .replace("{companyName}", legal.companyName)
      .replace("{tradingAs}", legal.tradingAs)
      .replace("{taxIdLabel}", legal.taxIdLabel)
      .replace("{taxId}", legal.taxId)
      .replace("{address}", legal.address)
      .replace("{email}", legal.email)
      .replace("{phone}", legal.phone) },
    { type: "p", text: i18n.t("We reserve the right to update this Policy from time to time. The version in force at the moment your order is placed governs that order.") },
  ];
}

export default function LegalRefundScreen() {
  const legal = Config.legal || {};
  const blocks = buildBlocks(legal);
  return (
    <SafeAreaView style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.body}>
          <LegalDocument
            title={i18n.t("Refund, return & cancellation policy")}
            effective={i18n.t("Effective date: 26 May 2026")}
            blocks={blocks}
          />
        </View>
        <PublicFooter />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#ffffff",
  },
  container: {
    flexGrow: 1,
  },
  body: {
    padding: 20,
    maxWidth: 720,
    width: "100%",
    alignSelf: "center",
  },
});
